import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  LifeBuoy,
  X,
  Send,
  MessageCircle,
  Mail,
  ArrowLeft,
  Plus,
  Loader2,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Captcha from "@/components/Captcha";
import { useHoneypot } from "@/components/Honeypot";

export default function SupportPill() {
  const { user, isAdmin } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Admins only see the console link while inside the admin area.
  // On the storefront they get the normal customer chat widget.
  if (isAdmin && location.pathname.startsWith("/admin")) {
    return (
      <div className="fixed bottom-5 right-5 z-50">
        <Link
          to="/admin/support"
          data-testid="support-pill-admin"
          className="group flex items-center gap-2 rounded-full border border-emerald-500/35 bg-[#0b2115]/90 px-4 py-2.5 text-sm font-medium text-white shadow-[0_18px_35px_-14px_rgba(52,211,153,0.6)] backdrop-blur transition-transform hover:-translate-y-0.5"
        >
          <LifeBuoy size={16} className="text-lime-300" />
          Support console
        </Link>
      </div>
    );
  }

  return (
    <div className="fixed bottom-5 right-5 z-50" data-testid="support-pill-root">
      {open && (user ? <UserChat onClose={() => setOpen(false)} /> : <GuestPanel onClose={() => setOpen(false)} />)}
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="support-pill-toggle"
        className="group flex items-center gap-2 rounded-full border border-emerald-500/35 bg-[#0b2115]/90 px-4 py-2.5 text-sm font-medium text-white shadow-[0_18px_35px_-14px_rgba(52,211,153,0.6)] backdrop-blur transition-transform hover:-translate-y-0.5"
        aria-label="Open support"
      >
        <LifeBuoy size={16} className="text-lime-300" />
        Support
        <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-emerald-400" />
      </button>
    </div>
  );
}

function GuestPanel({ onClose }) {
  const [form, setForm] = useState({ name: "", email: "", subject: "", body: "" });
  const [captcha, setCaptcha] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const hp = useHoneypot();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/support/guest", { ...form, captcha_token: captcha?.token, captcha_answer: captcha?.answer, ...hp.values() });
      setSent(true);
      toast.success("Message sent — we’ll reply by email");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally { setBusy(false); }
  };

  const inp = "w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white placeholder:text-emerald-100/40 focus:border-emerald-400/60 focus:outline-none";

  return (
    <div
      data-testid="support-panel-guest"
      className="mb-3 w-[340px] overflow-hidden rounded-2xl border border-emerald-500/25"
      style={{ background: "rgba(11,33,21,0.9)", backdropFilter: "blur(22px) saturate(140%)", WebkitBackdropFilter: "blur(22px) saturate(140%)" }}
    >
      <PanelHeader onClose={onClose} title="Send us a message" sub="No account needed — we’ll reply by email" />
      {sent ? (
        <div className="px-4 py-8 text-center">
          <p className="text-sm text-white">Thanks — your message is in.</p>
          <p className="mt-1 text-xs text-emerald-100/60">Our desk replies to <span className="text-lime-300">{form.email}</span>.</p>
          <Link to="/login" className="btn-edge-ghost mt-5 justify-center text-xs" data-testid="support-guest-signin">Have an account? Sign in to chat</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="grid gap-2.5 p-4">
          {hp.field}
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Your name" data-testid="support-guest-name" className={inp} />
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder="you@email.com" data-testid="support-guest-email" className={inp} />
          <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required placeholder="Subject" data-testid="support-guest-subject" className={inp} />
          <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required rows={4} placeholder="How can we help?" data-testid="support-guest-body" className={inp} />
          <div data-testid="support-guest-captcha"><Captcha onChange={setCaptcha} /></div>
          <button type="submit" disabled={busy} data-testid="support-guest-submit" className="btn-edge mt-1 justify-center text-sm">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send message
          </button>
          <a href="mailto:support@hkforexedge.com" className="mt-1 flex items-center justify-center gap-2 text-[11px] text-emerald-100/60 hover:text-lime-300" data-testid="support-email">
            <Mail size={12} /> or email support@hkforexedge.com
          </a>
        </form>
      )}
    </div>
  );
}

function PanelHeader({ onClose, title, sub, back }) {
  return (
    <div className="flex items-center justify-between border-b border-emerald-500/12 px-4 py-3">
      <div className="flex items-center gap-2">
        {back && (
          <button
            onClick={back}
            className="rounded-full p-1.5 text-emerald-100/60 hover:bg-white/5 hover:text-white"
            aria-label="Back"
          >
            <ArrowLeft size={14} />
          </button>
        )}
        <div>
          <p className="overline">Support</p>
          <p className="font-display text-sm font-medium text-white">{title}</p>
          {sub && <p className="text-[11px] text-emerald-100/60">{sub}</p>}
        </div>
      </div>
      <button
        onClick={onClose}
        className="rounded-full p-1.5 text-emerald-100/60 hover:bg-white/5 hover:text-white"
        data-testid="support-close"
        aria-label="Close"
      >
        <X size={16} />
      </button>
    </div>
  );
}

function UserChat({ onClose }) {
  const [view, setView] = useState("list"); // list | thread | new
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadThreads = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/support/threads/me");
      setThreads(data);
    } catch (e) {
      toast.error("Failed to load threads");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadThreads();
    const t = setInterval(loadThreads, 8000);
    return () => clearInterval(t);
  }, []);

  return (
    <div
      data-testid="support-panel-user"
      className="mb-3 flex h-[520px] w-[360px] flex-col overflow-hidden rounded-2xl border border-emerald-500/25"
      style={{
        background: "rgba(11,33,21,0.9)",
        backdropFilter: "blur(22px) saturate(140%)",
        WebkitBackdropFilter: "blur(22px) saturate(140%)",
      }}
    >
      {view === "list" && (
        <>
          <PanelHeader onClose={onClose} title="Your conversations" sub="Threaded chat with our team" />
          <div className="flex-1 overflow-y-auto p-3">
            {loading && <p className="p-3 text-xs text-emerald-100/50">Loading…</p>}
            {!loading && threads.length === 0 && (
              <div className="p-3 text-center">
                <p className="text-xs text-emerald-100/60">No conversations yet.</p>
              </div>
            )}
            <div className="grid gap-2">
              {threads.map((t) => (
                <button
                  key={t.id}
                  onClick={() => {
                    setActiveThread(t);
                    setView("thread");
                  }}
                  data-testid={`support-thread-${t.id}`}
                  className="rounded-lg border border-emerald-500/12 bg-[#0d2416]/60 p-3 text-left transition-colors hover:border-emerald-500/40"
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="truncate text-sm font-medium text-white">{t.subject}</p>
                    {t.unread_user > 0 && (
                      <span className="chip chip-lime !py-0.5 !text-[9px]">{t.unread_user}</span>
                    )}
                  </div>
                  <p className="mt-1 text-[11px] text-emerald-100/50">
                    {t.status} · {new Date(t.last_message_at).toLocaleString()}
                  </p>
                </button>
              ))}
            </div>
          </div>
          <div className="border-t border-emerald-500/12 p-3">
            <button
              onClick={() => setView("new")}
              data-testid="support-new-thread"
              className="btn-edge w-full justify-center text-sm"
            >
              <Plus size={14} /> New conversation
            </button>
          </div>
        </>
      )}
      {view === "new" && <NewThread onClose={onClose} back={() => setView("list")} onCreated={(t) => { setActiveThread(t); setView("thread"); loadThreads(); }} />}
      {view === "thread" && activeThread && (
        <ThreadView
          onClose={onClose}
          back={() => setView("list")}
          thread={activeThread}
          isAdmin={false}
        />
      )}
    </div>
  );
}

function NewThread({ onClose, back, onCreated }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/support/threads", { subject, body });
      toast.success("Conversation started");
      onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <PanelHeader onClose={onClose} back={back} title="Start a conversation" />
      <form onSubmit={submit} className="flex flex-1 flex-col p-4">
        <label className="mb-2 block text-[10px] font-medium uppercase tracking-widest text-emerald-100/60">
          Subject
        </label>
        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          required
          maxLength={200}
          data-testid="support-new-subject"
          className="mb-4 rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none"
          placeholder="License question, refund, technical…"
        />
        <label className="mb-2 block text-[10px] font-medium uppercase tracking-widest text-emerald-100/60">
          Message
        </label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          required
          rows={6}
          data-testid="support-new-body"
          className="flex-1 rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 p-3 text-sm text-white focus:border-emerald-400/60 focus:outline-none"
          placeholder="Tell us what's happening…"
        />
        <button
          type="submit"
          disabled={busy}
          data-testid="support-new-submit"
          className="btn-edge mt-4 justify-center text-sm"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          Send message
        </button>
      </form>
    </>
  );
}

export function ThreadView({ onClose, back, thread, isAdmin }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/support/threads/${thread.id}/messages`);
      setMsgs(data.messages);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [thread.id]);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [msgs.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/support/threads/${thread.id}/messages`, { body: text });
      setMsgs((m) => [...m, data]);
      setText("");
    } catch (e) {
      toast.error("Failed to send");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PanelHeader onClose={onClose} back={back} title={thread.subject} sub={isAdmin ? `${thread.user_email}` : `Thread with our team`} />
      <div ref={boxRef} className="flex-1 space-y-2.5 overflow-y-auto bg-[#061510]/40 p-4">
        {loading && <p className="text-xs text-emerald-100/50">Loading…</p>}
        {msgs.map((m) => {
          const mine = m.sender_id === user?.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[78%] px-3.5 py-2 text-sm ${mine ? "bubble-user" : "bubble-admin"}`}>
                <p className="whitespace-pre-wrap">{m.body}</p>
                <p className={`mt-1 text-[10px] ${mine ? "text-[#04150c]/70" : "text-emerald-100/50"}`}>
                  {m.sender_name} · {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={send} className="flex items-center gap-2 border-t border-emerald-500/12 p-3">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type a message…"
          data-testid="support-msg-input"
          className="flex-1 rounded-full border border-emerald-500/15 bg-[#0d2416]/60 px-4 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none"
        />
        <button
          disabled={busy}
          data-testid="support-msg-send"
          className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-b from-emerald-400 to-emerald-500 text-emerald-950 transition-transform hover:-translate-y-0.5 disabled:opacity-50"
          aria-label="Send"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </button>
      </form>
    </>
  );
}
